import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboard } from '../context/DashboardContext';
import { ReactComponent as Logo } from '../logo.svg';
import { reportesMenu, configuracionesMenu, organizacionesMenu } from '../constants/permisosMenus';
import { collectReadablePermTitles, normalizePermTitle } from '../utils/permisosTree';
import './Layout.css';

const normalizeTitle = normalizePermTitle;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { sucursales, selectedSucursalId, selectSucursal } = useDashboard();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sucExpanded, setSucExpanded] = useState(true);
  const [reportesExpanded, setReportesExpanded] = useState(true);
  const [pvExpanded, setPvExpanded] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  const [orgExpanded, setOrgExpanded] = useState(false);
  const permisosModulos = useMemo(
    () => (Array.isArray(user?.PermisosModel) ? user.PermisosModel : []),
    [user]
  );

  // Todos los reportes visibles en el menú; acceso por ruta en ProtectedRoute
  const allowedReportes = useMemo(() => reportesMenu, []);

  const allowedConfigItems = useMemo(() => {
    const titleToItem = new Map(
      configuracionesMenu.map((item) => [normalizeTitle(item.label), item])
    );
    const readableTitles = collectReadablePermTitles(permisosModulos);
    const found = new Set();
    for (const title of readableTitles) {
      const hit = titleToItem.get(title);
      if (hit) found.add(hit.key);
      if (title === 'cuentas') found.add('usuarios');
    }
    return configuracionesMenu.filter((item) => found.has(item.key));
  }, [permisosModulos]);

  const PV_MENU = [
    { key: 'pv-cancelaciones', label: 'Cancelaciones', route: '/mod-reportes/cancelaciones', permKey: 'cancelaciones' },
    { key: 'pv-gastos', label: 'Gastos', route: '/mod-reportes/gastos', permKey: 'gastos' },
    { key: 'pv-descuentos',    label: 'Descuentos',    route: '/mod-reportes/descuentos-cortesias', permKey: 'descuentos-cortesias' },
    { key: 'pv-cortesia',      label: 'Cortesía',      route: '/mod-reportes/descuentos-cortesias', permKey: 'descuentos-cortesias' },
  ];

  const allowedPvItems = useMemo(
    () => PV_MENU.filter((item) => allowedReportes.some((r) => r.key === item.permKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedReportes]
  );

  const allowedOrgItems = useMemo(() => {
    const titleToItem = new Map([
      ['empresa', organizacionesMenu.find((item) => item.key === 'empresas')],
      ['empresas', organizacionesMenu.find((item) => item.key === 'empresas')],
      ['sucursal', organizacionesMenu.find((item) => item.key === 'sucursales')],
      ['sucursales', organizacionesMenu.find((item) => item.key === 'sucursales')],
    ]);
    const readableTitles = collectReadablePermTitles(permisosModulos);
    const found = new Set();
    for (const title of readableTitles) {
      const hit = titleToItem.get(title);
      if (hit) found.add(hit.key);
    }
    return organizacionesMenu.filter((item) => found.has(item.key));
  }, [permisosModulos]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const nombre = user?.nombreCompleto || user?.nombreUsuario || 'Usuario';

  return (
    <div className="app-layout">
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}
      <aside className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Logo className="sidebar-logo-svg" />
          </div>
          <button type="button" className="sidebar-close" onClick={closeSidebar} aria-label="Cerrar menú">
            ✕
          </button>
        </div>
        <nav className="sidebar-nav">
          <button
            type="button"
            className={`sidebar-nav-btn${!selectedSucursalId ? ' active' : ''}`}
            onClick={() => {
              selectSucursal(null);
              navigate('/dashboard');
              closeSidebar();
            }}
          >
            Dashboard General
          </button>

          {allowedReportes.length > 0 && (
            <>
              <button
                type="button"
                className="sidebar-nav-group"
                onClick={() => setReportesExpanded((v) => !v)}
              >
                <span>Reportes</span>
                <span className={`sidebar-nav-arrow${reportesExpanded ? ' open' : ''}`}>›</span>
              </button>
              {reportesExpanded && (
                <div className="sidebar-reportes-list">
                  {allowedReportes.map((item) => {
                    const isActive = location.pathname === `/mod-reportes/${item.key}`;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`sidebar-reportes-item${isActive ? ' active' : ''}`}
                        onClick={() => {
                          navigate(`/mod-reportes/${item.key}`);
                          closeSidebar();
                        }}
                      >
                        <span className="sidebar-reportes-icon">◉</span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {false && allowedPvItems.length > 0 && (
            <>
              <button
                type="button"
                className="sidebar-nav-group"
                onClick={() => setPvExpanded((v) => !v)}
              >
                <span>Punto de Venta</span>
                <span className={`sidebar-nav-arrow${pvExpanded ? ' open' : ''}`}>›</span>
              </button>
              {pvExpanded && (
                <div className="sidebar-reportes-list">
                  {allowedPvItems.map((item) => {
                    const isActive = location.pathname === item.route;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`sidebar-reportes-item${isActive ? ' active' : ''}`}
                        onClick={() => {
                          navigate(item.route);
                          closeSidebar();
                        }}
                      >
                        <span className="sidebar-reportes-icon">◉</span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {allowedConfigItems.length > 0 && (
            <>
              <button
                type="button"
                className="sidebar-nav-group"
                onClick={() => setConfigExpanded((v) => !v)}
              >
                <span>Seguridad</span>
                <span className={`sidebar-nav-arrow${configExpanded ? ' open' : ''}`}>›</span>
              </button>
              {configExpanded && (
                <div className="sidebar-reportes-list">
                  {allowedConfigItems.map((item) => {
                    const isActive = location.pathname === `/seguridad/${item.key}`;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`sidebar-reportes-item${isActive ? ' active' : ''}`}
                        onClick={() => {
                          navigate(`/seguridad/${item.key}`);
                          closeSidebar();
                        }}
                      >
                        <span className="sidebar-reportes-icon">◉</span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {allowedOrgItems.length > 0 && (
            <>
              <button
                type="button"
                className="sidebar-nav-group"
                onClick={() => setOrgExpanded((v) => !v)}
              >
                <span>Organizaciones</span>
                <span className={`sidebar-nav-arrow${orgExpanded ? ' open' : ''}`}>›</span>
              </button>
              {orgExpanded && (
                <div className="sidebar-reportes-list">
                  {allowedOrgItems.map((item) => {
                    const isActive = location.pathname === `/organizaciones/${item.key}`;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`sidebar-reportes-item${isActive ? ' active' : ''}`}
                        onClick={() => {
                          navigate(`/organizaciones/${item.key}`);
                          closeSidebar();
                        }}
                      >
                        <span className="sidebar-reportes-icon">◉</span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {sucursales.length > 0 && (
            <>
              <button
                type="button"
                className="sidebar-nav-group"
                onClick={() => setSucExpanded((v) => !v)}
              >
                <span>Sucursales</span>
                <span className={`sidebar-nav-arrow${sucExpanded ? ' open' : ''}`}>›</span>
              </button>
              {sucExpanded && sucursales.map((suc) => (
                <button
                  key={suc.sucursalId}
                  type="button"
                  className={`sidebar-nav-btn sidebar-nav-btn--sub${selectedSucursalId === suc.sucursalId ? ' active' : ''}`}
                  onClick={() => {
                    selectSucursal(suc.sucursalId);
                    navigate('/dashboard');
                    closeSidebar();
                  }}
                >
                  {suc.nombreSucursal}
                </button>
              ))}
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="main-content">
        <header className="header">
          <button type="button" className="header-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú">
            <span /><span /><span />
          </button>
          <div className="header-logo-mobile">
            <Logo className="header-logo-mobile-svg" />
          </div>
          <div className="header-spacer" />
          <div className="header-user-mobile">
            <button
              type="button"
              className="sidebar-user-avatar header-avatar-btn"
              onClick={() => navigate('/perfil')}
              aria-label="Mi perfil"
            >
              {nombre.charAt(0)}
            </button>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
}
